import type { Payout } from '../types/payout';

export interface TokenSummary {
  crypto: {
    id: string;
    symbol: string;
    name: string;
  };
  totalAmount: number;
  totalValueAtPurchase: number; // Valeur totale au moment du minage
  currentPrice: number; // Prix actuel
  currentTotalValue: number; // Valeur totale actuelle
  averagePurchasePrice: number; // Prix moyen d'acquisition
  profitLoss: number; // Plus/moins value
  profitLossPercentage: number; // Plus/moins value en %
  totalBNCAmount: number; // Montant total BNC (34% sur chaque payout individuel)
  totalSaleProceeds: number; // Produit de cession total (EUR reçus des ventes)
  totalRealizedCostBasis: number; // Base de coût des coins vendus (pour P&L réalisé)
  payouts: Payout[]; // Liste des payouts pour ce token
}

export const aggregateTokens = (payouts: Payout[], bncRate: number = 0.34): Map<string, TokenSummary> => {
  const tokenMap = new Map<string, TokenSummary>();

  // Sort by date to ensure acquisitions are processed before sales
  const sortedPayouts = [...payouts].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  sortedPayouts.forEach((payout) => {
    // Handle swap transactions
    if (payout.type === 'swap') {
      // Process received crypto
      if (payout.receivedCrypto && payout.receivedAmount) {
        const tokenId = payout.receivedCrypto.id;

        if (tokenMap.has(tokenId)) {
          const existing = tokenMap.get(tokenId)!;
          existing.totalAmount += payout.receivedAmount;
          existing.totalValueAtPurchase += payout.receivedTotalValue || 0;
          existing.averagePurchasePrice = existing.totalValueAtPurchase / existing.totalAmount;
          existing.payouts.push(payout);
        } else {
          tokenMap.set(tokenId, {
            crypto: payout.receivedCrypto,
            totalAmount: payout.receivedAmount,
            totalValueAtPurchase: payout.receivedTotalValue || 0,
            currentPrice: 0,
            currentTotalValue: 0,
            averagePurchasePrice: payout.receivedUnitPrice || 0,
            profitLoss: 0,
            profitLossPercentage: 0,
            totalBNCAmount: 0,
            totalSaleProceeds: 0,
            totalRealizedCostBasis: 0,
            payouts: [payout],
          });
        }
      }

      // Subtract sent crypto from holdings and track realized P&L
      if (payout.sentCrypto && payout.sentAmount) {
        const sentTokenId = payout.sentCrypto.id;
        if (tokenMap.has(sentTokenId)) {
          const existing = tokenMap.get(sentTokenId)!;
          const sentAmount = payout.sentAmount;
          const saleProceeds = payout.sentTotalValue || 0;
          if (existing.totalAmount > 0 && sentAmount > 0) {
            const ratio = Math.min(sentAmount / existing.totalAmount, 1);
            const costBasisSold = existing.totalValueAtPurchase * ratio;
            existing.totalSaleProceeds += saleProceeds;
            existing.totalRealizedCostBasis += costBasisSold;
            existing.totalValueAtPurchase -= costBasisSold;
            existing.totalAmount = Math.max(0, existing.totalAmount - sentAmount);
          }
        }
      }
      return;
    }

    // Handle sales - subtract sold amount from holdings and track proceeds
    if (payout.type === 'vente') {
      const tokenId = payout.crypto?.id;
      if (tokenId && tokenMap.has(tokenId)) {
        const existing = tokenMap.get(tokenId)!;
        const soldAmount = payout.amount || 0;
        const saleProceeds = payout.totalValue || 0;
        if (existing.totalAmount > 0 && soldAmount > 0) {
          const ratio = Math.min(soldAmount / existing.totalAmount, 1);
          const costBasisSold = existing.totalValueAtPurchase * ratio;
          existing.totalSaleProceeds += saleProceeds;
          existing.totalRealizedCostBasis += costBasisSold;
          existing.totalValueAtPurchase -= costBasisSold;
          existing.totalAmount = Math.max(0, existing.totalAmount - soldAmount);
        }
        // Keep the entry to show realized P&L even if fully sold
      }
      return;
    }

    // Transfers are just wallet movements — don't add to portfolio
    if (payout.type === 'transfert') {
      return;
    }

    // Handle normal acquisitions (payout, achat, stacking)
    const tokenId = payout.crypto!.id;
    const isAchat = payout.type === 'achat';
    const shouldApplyBNC = !isAchat; // Apply BNC to payout and stacking, not to purchases or sales

    if (tokenMap.has(tokenId)) {
      const existing = tokenMap.get(tokenId)!;
      existing.totalAmount += payout.amount || 0;
      existing.totalValueAtPurchase += payout.totalValue || 0;
      existing.averagePurchasePrice = existing.totalValueAtPurchase / existing.totalAmount;
      existing.payouts.push(payout);
      // Recalculer le BNC total avec le taux configuré - EXCLUDE "achat" type
      existing.totalBNCAmount = existing.payouts.reduce((total, p) => {
        // Only count payout and stacking types
        return total + (p.type === 'payout' || p.type === 'stacking' ? (p.totalValue || 0) * bncRate : 0);
      }, 0);
    } else {
      tokenMap.set(tokenId, {
        crypto: payout.crypto!,
        totalAmount: payout.amount || 0,
        totalValueAtPurchase: payout.totalValue || 0,
        currentPrice: 0,
        currentTotalValue: 0,
        averagePurchasePrice: payout.unitPrice || 0,
        profitLoss: 0,
        profitLossPercentage: 0,
        totalBNCAmount: shouldApplyBNC ? (payout.totalValue || 0) * bncRate : 0,
        totalSaleProceeds: 0,
        totalRealizedCostBasis: 0,
        payouts: [payout],
      });
    }
  });

  return tokenMap;
};
